import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { fetchMyUsage, type Usage as UsageData } from "../api";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT, type Messages } from "../i18n";
import { useSession } from "../session";

/**
 * Usage is a receipt for this month: how much of your library's budget is
 * spent, what it went on, and the latest charges.
 *
 * Every account has one, readers included — a reader's questions are billed to
 * their own library (ADR-0013), and someone who is charged has to be able to
 * see it before a refusal arrives.
 */
export default function Usage() {
  const { isSignedIn, isPending: sessionPending } = useSession();
  const t = useT();
  const usage = useQuery({
    queryKey: ["usage", "me"],
    queryFn: fetchMyUsage,
    enabled: isSignedIn,
  });

  if (!sessionPending && !isSignedIn) return <Navigate to="/" replace />;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8">
        <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
          {t.usage.title}
        </h1>
        <p className="mt-1 text-sm text-muted" data-testid="usage-period">
          {usage.data ? t.usage.resets(periodLabel(usage.data, t)) : t.usage.thisMonth}
        </p>
      </header>
      <UsageState
        isPending={sessionPending || usage.isPending}
        isError={usage.isError}
        onRetry={() => void usage.refetch()}
        data={usage.data}
      />
    </main>
  );
}

/** periodLabel names the month a usage body covers, e.g. "September 2026". */
export function periodLabel(data: UsageData, t: Messages): string {
  return new Date(data.period_start).toLocaleDateString(t.meta.dateLocale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * UsageState renders a usage body with its loading and error states. Shared by
 * your own usage page and the admin's view of somebody else's.
 */
export function UsageState({
  isPending,
  isError,
  onRetry,
  data,
}: {
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  data?: UsageData;
}) {
  const t = useT();
  if (isPending) {
    return (
      <Pending label={t.common.loading}>
        <Placeholder className="h-36 rounded-card" />
      </Pending>
    );
  }
  if (isError || !data) {
    return (
      <p role="alert" className="text-sm text-red-600 dark:text-red-400">
        {t.usage.loadFailed}{" "}
        <button type="button" onClick={onRetry} className="underline">
          {t.common.tryAgain}
        </button>
      </p>
    );
  }
  return <UsageView data={data} />;
}

function UsageView({ data }: { data: UsageData }) {
  const t = useT();
  const used = data.limit_micros > 0 ? Math.min(1, data.spent_micros / data.limit_micros) : 0;
  // The server names what was bought in English and with a stable key; the key
  // is what gets translated, and the English is the fallback for a kind this
  // client does not know yet.
  const kind = (key: string, label: string) => t.usage.kinds[key] ?? label;

  return (
    <>
      <section
        data-testid="usage-budget"
        className="rounded-card border border-line bg-surface p-5"
      >
        <div className="flex items-baseline justify-between gap-3">
          {/* Dollar amounts stay left to right inside a Persian page. */}
          <p
            dir="ltr"
            data-testid="usage-spent"
            className="text-3xl font-semibold tracking-tight tabular-nums"
          >
            {data.spent_usd}
          </p>
          <p className="text-sm text-muted">
            {/* Isolated, or the dollar sign swaps ends inside a Persian line
                and "$25.00" reads "25.00$" (CLAUDE.md §Persian and RTL). */}
            {t.usage.of}{" "}
            <bdi dir="ltr" data-testid="usage-limit">
              {data.limit_usd}
            </bdi>
          </p>
        </div>
        <div
          role="progressbar"
          aria-label={t.usage.budgetUsed}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(used * 100)}
          className="mt-4 h-2 overflow-hidden rounded-full bg-sunk"
        >
          <div
            // Any spend shows as a sliver: a bar that stays empty after a
            // charge reads as "nothing was recorded".
            style={{
              width: `${data.spent_micros > 0 ? Math.max(used * 100, 1.5) : 0}%`,
            }}
            className={`h-full rounded-full ${used >= 0.8 ? "bg-amber-500" : "bg-sage"}`}
          />
        </div>
        <p className="mt-3 text-sm text-ink-soft">{t.usage.left(data.remaining_usd)}</p>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 text-sm font-medium text-ink-soft">{t.usage.wentOn}</h2>
        {data.by_what.length === 0 ? (
          <p data-testid="usage-empty" className="text-sm text-muted">
            {t.usage.nothingSpent}
          </p>
        ) : (
          <ul
            data-testid="usage-by-what"
            className="divide-y divide-line rounded-card border border-line bg-surface"
          >
            {data.by_what.map((line) => (
              <li
                key={line.key}
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <span>
                  {kind(line.key, line.label)}{" "}
                  <span className="text-muted">× {t.meta.num(line.count)}</span>
                </span>
                <span className="tabular-nums">{line.cost_usd}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.recent.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-medium text-ink-soft">{t.usage.recent}</h2>
          <ul
            data-testid="usage-recent"
            className="divide-y divide-line rounded-card border border-line bg-surface"
          >
            {data.recent.map((charge, i) => (
              <li
                key={`${charge.at}-${i}`}
                className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate">{kind(charge.key, charge.label)}</span>
                  <span className="block text-xs text-muted">{formatWhen(charge.at, t)}</span>
                </span>
                <span className="tabular-nums text-ink-soft">{charge.cost_usd}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.mine_micros !== data.spent_micros && (
        <p className="mt-6 text-xs text-muted">{t.usage.others(data.mine_usd)}</p>
      )}
    </>
  );
}

function formatWhen(iso: string, t: Messages): string {
  return new Date(iso).toLocaleString(t.meta.dateLocale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
